/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { AuthService } from '../../../service/auth.service';
import { PortfolioApiService } from 'src/app/shared/services/customer-api/portfolio-api.service';
import { YukkApi } from '../../../service/yukkapi.service';
import { ScoreTimeSeriesService } from 'src/app/shared/services/data-api/score-time-series.service';
import { ScoreUtilsService } from 'src/app/shared/utils/score-utils.service';
import { DateUtils } from 'src/app/shared/utils/date.utils';
import { SentimentService } from 'src/app/shared/services/data-api/sentiment.service';
import {
  ScoreTimeSeriesResponse,
  ScoreRequestTypes,
} from 'src/app/shared/models/score-time-series.interface';

interface ScoreResponse {
  score: number | null;
  trend_value?: string;
  trend_type?: 'bear' | 'bull';
  sentiment_delta?: number;
  score_ts?: { score: number }[];
}

export interface AuditData {
  entity: {
    alpha_id?: string;
    name: string;
    type: string;
    description?: any;
    sanctions?: any[];
  };
  sentiment?: {
    sentiment?: number;
    sentiment_delta?: number;
  };
  score?: number;
  trend_value?: string;
  trend_type?: 'bear' | 'bull';
  industryBenchmarkScore?: number;
  industryBenchmarkDescription?: string;
  showWarningFlag?: boolean;
  showCautionFlag?: boolean;
  flagScore?: number;
}

interface FlagData {
  showFlag: boolean;
  flagScore?: number;
}

const BENCHMARK_SCORES = [
  'esg',
  'eb_sim',
  'eb_sim_risk',
  'general_risk',
  'esg_risk',
  'immediate_risk',
  'credit_risk',
  'bb',
];

const BENCHMARK_LABELS: Record<number, string> = {
  0: 'Laggard',
  1: 'Below average',
  2: 'Above average',
  3: 'Leader',
};

@Injectable({
  providedIn: 'root',
})
export class AuditDataService {
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  readonly loading$ = this.loadingSubject.asObservable();

  constructor(
    private yukkApi: YukkApi,
    private auth: AuthService,
    private scoreTimeSeriesService: ScoreTimeSeriesService,
    private scoreUtils: ScoreUtilsService,
    private sentimentService: SentimentService,
    private portfolioApi: PortfolioApiService,
  ) {}

  public getAuditData(params: any, baseData: AuditData): Observable<AuditData> {
    this.loadingSubject.next(true);

    const defaultScore = this.auth.scorelabSettings.defaultScore;
    const isSentiment = defaultScore === 'sentiment';
    const isBullBear = defaultScore === 'bb';

    const period = params.period || '7';

    const scoreType: ScoreRequestTypes = isSentiment
      ? { period }
      : this.scoreUtils.resolveScoreType(defaultScore);

    const score$ = this.fetchScore(scoreType, params);
    const entityInfo$ = this.getEntityInformation(params);
    const industryBenchmark$ = this.getIndustryBenchmark(defaultScore, params);

    return forkJoin({
      scoreResult: score$,
      benchmarkResult: industryBenchmark$,
      entityInfo: entityInfo$,
    }).pipe(
      switchMap(({ scoreResult, benchmarkResult, entityInfo }) => {
        const warningFlag$ = this.getWarningFlagData(params, entityInfo);
        const cautionFlag$ = this.getCautionFlagData(params, entityInfo);

        return forkJoin({
          warningFlagData: warningFlag$,
          cautionFlagData: cautionFlag$,
        }).pipe(
          map(({ warningFlagData, cautionFlagData }) => {
            const auditData = { ...baseData, ...entityInfo };

            if (scoreResult) {
              if (isSentiment) {
                delete auditData.score;
                auditData.sentiment = {
                  sentiment: scoreResult.score,
                  sentiment_delta: scoreResult.sentiment_delta,
                };
              } else if (isBullBear) {
                auditData.score = scoreResult.score;
                auditData.trend_value = scoreResult.trend_value;
                auditData.trend_type = scoreResult.trend_type;
              } else {
                auditData.score =
                  scoreResult.score !== null
                    ? Number(scoreResult.score.toFixed(1))
                    : null;
              }
            }

            this.processBenchmark(auditData, benchmarkResult);
            auditData.showWarningFlag = warningFlagData.showFlag;
            auditData.flagScore = warningFlagData.flagScore;
            auditData.showCautionFlag = cautionFlagData.showFlag;

            return auditData;
          }),
        );
      }),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  private getIndustryBenchmark(
    scoreType: string,
    params: any,
  ): Observable<ScoreResponse | null> {
    if (!BENCHMARK_SCORES.includes(scoreType) || params.type !== 'company') {
      return of(null);
    }

    return this.yukkApi
      .scoresTimeSeries(
        'score',
        { ...params, requestScore: scoreType, industry_bench: true },
        'chart',
      )
      .pipe(
        map((res: any) => ({ score: res.score_ts_last_score?.score ?? null })),
        catchError(() => of(null)),
      );
  }

  private processBenchmark(
    auditData: AuditData,
    benchmark: ScoreResponse | null,
  ): void {
    if (!benchmark || benchmark.score === null) {
      auditData.industryBenchmarkScore = null;
      auditData.industryBenchmarkDescription = '';
      return;
    }

    auditData.industryBenchmarkScore = benchmark.score;
    const label = BENCHMARK_LABELS[benchmark.score] || '';
    const industry =
      auditData.entity?.description?.Industry?.name || 'Industry';
    auditData.industryBenchmarkDescription = label
      ? `${label} in ${industry}`
      : '';
  }

  public getEntityInformation(params: any): Observable<AuditData> {
    return new Observable((observer) => {
      this.sentimentService
        .getSentiment({ ...params, getSanctions: true }, false)
        .subscribe({
          next: (results: any) => {
            const dataEntity: AuditData = { ...results };

            if (
              results.entity.type === 'portfolio' &&
              Object.prototype.hasOwnProperty.call(this.auth, 'folio')
            ) {
              dataEntity.entity = {
                ...results.entity,
                name: this.portfolioApi.activePortfolio.name || '',
              };
            }

            if (params.type === 'pne') {
              const [prefix, suffix] = dataEntity.entity.alpha_id.split('.');
              dataEntity.entity.name = `${suffix} (${prefix})`;
            } else if (
              this.portfolioApi.activePortfolio?.marketPortfolio &&
              ['industry', 'supersector', 'sector', 'subsector'].includes(
                params.type,
              )
            ) {
              dataEntity.entity.type = params.type;
              dataEntity.entity.name =
                this.portfolioApi.activePortfolio.marketPortfolioName;
            }

            observer.next(dataEntity);
            observer.complete();
          },
          error: (error) => {
            console.error('Error fetching entity information:', error);
            observer.error(error);
          },
        });
    });
  }

  public getWarningFlagData(
    params: any,
    auditData: AuditData,
  ): Observable<FlagData> {
    const defaultFlag = this.auth.scorelabSettings.defaultFlag;

    if (!this.shouldCheckFlag(defaultFlag, auditData)) {
      return of({ showFlag: false });
    }

    if (defaultFlag.score_type === 'sentiment') {
      const currentScore = (auditData.sentiment?.sentiment ?? 0) * 100;
      return of(this.checkThreshold(currentScore, defaultFlag));
    }

    const resolvedScore = this.scoreUtils.resolveScoreType(
      defaultFlag.score_type,
    );
    return this.fetchScore(resolvedScore, params).pipe(
      map((res) => this.checkThreshold(res.score, defaultFlag)),
      catchError(() => of({ showFlag: false })),
    );
  }

  public getCautionFlagData(
    params: any,
    auditData: AuditData,
  ): Observable<FlagData> {
    const defaultFlag = this.auth.scorelabSettings.defaultFlag;

    if (!this.shouldCheckFlag(defaultFlag, auditData)) {
      return of({ showFlag: false });
    }

    const time =
      params.time && DateUtils.fromTo(params.time).period < 365
        ? params.time
        : 365;

    const timeValue = typeof time === 'number' ? time : parseInt(time, 10);
    const isSentiment = defaultFlag.score_type === 'sentiment';

    const scoreType: ScoreRequestTypes = isSentiment
      ? { period: timeValue <= 7 ? 1 : timeValue <= 30 ? 7 : 30 }
      : this.scoreUtils.resolveScoreType(defaultFlag.score_type);

    return this.fetchScore(scoreType, { ...params, time: String(time) }).pipe(
      map((res) => {
        const thresholdReached = (res.score_ts || []).some((item) => {
          const score = isSentiment ? item.score * 100 : item.score;
          return this.checkThreshold(score, defaultFlag).showFlag;
        });
        return { showFlag: thresholdReached };
      }),
      catchError(() => of({ showFlag: false })),
    );
  }

  private shouldCheckFlag(defaultFlag: any, auditData: AuditData): boolean {
    return (
      !!auditData?.entity &&
      !!defaultFlag &&
      defaultFlag.entity_type?.includes(auditData.entity.type)
    );
  }

  private checkThreshold(score: number | null, flag: any): FlagData {
    if (score === null || score === undefined) {
      return { showFlag: false };
    }

    const isAbove =
      flag.threshold_setting === 'ABOVE_EQUAL' && score >= flag.threshold;
    const isBelow =
      flag.threshold_setting === 'BELOW_EQUAL' && score <= flag.threshold;

    return isAbove || isBelow
      ? { showFlag: true, flagScore: score }
      : { showFlag: false };
  }

  private fetchScore(
    scoreType: ScoreRequestTypes,
    params: any,
  ): Observable<ScoreResponse> {
    return this.scoreTimeSeriesService
      .getScoreTimeSeries({
        customParams: { collapse_on: ['participant'] },
        body: {
          score_type: scoreType,
          time: params.time || '7',
        },
      })
      .pipe(
        map((response: ScoreTimeSeriesResponse) => {
          const timeSeries = response.score_ts?.[0]?.score_ts || [];
          const lastScore = ScoreUtilsService.getLastScore(timeSeries);
          const { trendType, trendValue } =
            ScoreUtilsService.getBullBearTrend(lastScore);

          let sentimentDelta = 0;
          if (lastScore != null && timeSeries.length >= 2) {
            for (let i = timeSeries.length - 1; i >= 1; i--) {
              if (timeSeries[i].score != null) {
                const prevScore = timeSeries[i - 1]?.score;
                if (prevScore != null) {
                  sentimentDelta = timeSeries[i].score - prevScore;
                }
                break;
              }
            }
          }

          return {
            score: lastScore,
            trend_value: trendValue,
            trend_type: trendType,
            sentiment_delta: sentimentDelta,
            score_ts: timeSeries.map((item) => ({ score: item.score })),
          };
        }),
        catchError(() => of({ score: null, score_ts: [] })),
      );
  }
}
